import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { debounce } from '@theme/utilities';

/** @type {string} */
const CART_DRAWER_OVERLAY_UPDATED_EVENT = 'cart-drawer-overlay:updated';

/** @type {MutationObserver | undefined} */
let overlaySourceObserver;

/**
 * @param {Event} event
 * @returns {boolean}
 */
function isCartDrawerOverlayEditorEvent(event) {
  const sectionId =
    event instanceof CustomEvent && event.detail && typeof event.detail.sectionId === 'string'
      ? event.detail.sectionId
      : undefined;

  if (sectionId) {
    return Boolean(
      document.getElementById(`shopify-section-${sectionId}`)?.querySelector('[data-cart-drawer-overlay-announcement]')
    );
  }

  if (!(event.target instanceof HTMLElement)) return false;

  return Boolean(
    event.target.hasAttribute('data-cart-drawer-overlay-announcement') ||
      event.target.querySelector('[data-cart-drawer-overlay-announcement]') ||
      event.target.closest('[data-cart-drawer-overlay-announcement]')
  );
}

/**
 * @returns {void}
 */
function syncAllCartDrawerAnnouncements() {
  document.querySelectorAll('cart-drawer-component').forEach((drawer) => {
    if (drawer instanceof CartDrawerComponent) {
      drawer.syncAnnouncement();
    }
  });
}

const debouncedSyncAllCartDrawerAnnouncements = debounce(syncAllCartDrawerAnnouncements, 50);

/**
 * @returns {void}
 */
function observeOverlaySource() {
  overlaySourceObserver?.disconnect();

  const source = document.querySelector('#overlay-group [data-cart-drawer-overlay-announcement]');
  if (!(source instanceof HTMLElement)) return;

  overlaySourceObserver = new MutationObserver(() => {
    debouncedSyncAllCartDrawerAnnouncements();
  });

  overlaySourceObserver.observe(source, {
    attributes: true,
    childList: true,
    subtree: true,
    characterData: true,
  });
}

/**
 * @param {Event} event
 * @returns {void}
 */
function handleCartDrawerOverlayEditorChange(event) {
  if (!isCartDrawerOverlayEditorEvent(event)) return;

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      syncAllCartDrawerAnnouncements();
      observeOverlaySource();
    });
  });
}

if (Shopify?.designMode) {
  document.addEventListener('shopify:section:load', handleCartDrawerOverlayEditorChange);
  document.addEventListener('shopify:section:reorder', handleCartDrawerOverlayEditorChange);
  document.addEventListener('shopify:block:select', handleCartDrawerOverlayEditorChange);
  document.addEventListener('shopify:block:deselect', handleCartDrawerOverlayEditorChange);
  document.addEventListener(CART_DRAWER_OVERLAY_UPDATED_EVENT, handleCartDrawerOverlayEditorChange);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeOverlaySource, { once: true });
  } else {
    observeOverlaySource();
  }
}

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {AbortController | null} */
  #trendingSyncController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    window.addEventListener('resize', this.#scheduleTrendingCarouselSync, { passive: true });
    this.syncAnnouncement();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    window.removeEventListener('resize', this.#scheduleTrendingCarouselSync);
    this.#trendingSyncController?.abort();
  }

  /**
   * Clones the overlay-group announcement into the drawer mount so cart morphs
   * do not destroy the source section markup.
   */
  syncAnnouncement() {
    const mount = this.querySelector('[data-cart-drawer-announcement-mount]');
    const sourceBar = document.querySelector(
      '#overlay-group [data-cart-drawer-overlay-announcement] .cart-drawer__announcement-bar'
    );

    if (!(mount instanceof HTMLElement)) return;

    if (!(sourceBar instanceof HTMLElement)) {
      mount.replaceChildren();
      return;
    }

    mount.innerHTML = sourceBar.outerHTML;
    window.dispatchEvent(new Event('resize'));
  }

  #handleCartUpdate = () => {
    requestAnimationFrame(() => {
      this.syncAnnouncement();
      this.#scheduleTrendingCarouselSync();
    });
  };

  /**
   * Auto-open only for real add-to-cart actions.
   * `CartAddEvent` shares the `cart:update` name with quantity edits, morphs,
   * and theme-editor section reloads — those must not open the drawer.
   * @param {Event} event
   */
  #handleCartAdd = (event) => {
    if (!this.hasAttribute('auto-open')) return;
    // Theme editor refreshes/morphs fire cart:update on every save/click
    if (window.Shopify?.designMode) return;
    // Wishlist close sits over the cart icon — ignore opens during ghost-click guard.
    if (document.documentElement.hasAttribute('data-pz-suppress-cart-open')) return;

    const detail = event && 'detail' in event ? event.detail : null;
    const data = detail && typeof detail === 'object' && 'data' in detail ? detail.data : null;
    const source =
      data && typeof data === 'object' && data && 'source' in data && typeof data.source === 'string'
        ? data.source
        : '';

    const addSources = new Set([
      'product-form-component',
      'complete-look-component',
      'wishlist-drawer',
      'quick-add',
    ]);

    if (!addSources.has(source)) return;

    this.open();
  };

  open() {
    if (document.documentElement.hasAttribute('data-pz-suppress-cart-open')) return;
    this.showDialog();
    requestAnimationFrame(() => {
      this.syncAnnouncement();
      this.#scheduleTrendingCarouselSync();
    });
    document.dispatchEvent(new CustomEvent('cart-drawer:open'));
    // Scope to this drawer only — never remount PLP/homepage cards on open.
    document.dispatchEvent(
      new CustomEvent('pz-collection-cards:mount', { detail: { root: this } })
    );

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  /**
   * Re-check overflow after layout settles (cards/images can change scrollWidth).
   */
  #scheduleTrendingCarouselSync = () => {
    this.#trendingSyncController?.abort();
    const controller = new AbortController();
    this.#trendingSyncController = controller;

    const run = () => {
      if (controller.signal.aborted) return;
      this.#syncTrendingCarouselOverflow();
    };

    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });
    setTimeout(run, 120);
    setTimeout(run, 350);
  };

  /**
   * Show cart trending carousel arrow icons only when slides overflow the drawer width.
   */
  #syncTrendingCarouselOverflow = () => {
    this.querySelectorAll('.cart-drawer__trending-carousel').forEach((carousel) => {
      if (!(carousel instanceof HTMLElement)) return;

      const scroller = carousel.querySelector('slideshow-slides');
      const nav = carousel.querySelector('.cart-drawer__trending-nav');
      if (!(scroller instanceof HTMLElement)) return;

      // Force a layout read after cards mount
      const overflows = scroller.scrollWidth > scroller.clientWidth + 2;
      carousel.classList.toggle('is-overflow', overflows);

      if (nav instanceof HTMLElement) {
        if (overflows) {
          nav.removeAttribute('hidden');
          nav.style.display = '';
        } else {
          nav.setAttribute('hidden', '');
        }
      }
    });
  };

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
